import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const DESKTOP_QUERY = '(min-width: 901px)';
const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea';

const navLinkClass = ({ isActive }) => `nav-link${isActive ? ' is-active' : ''}`;

const Navbar = () => {
  const { authData, isAuthenticated, logout } = useAuth();
  const { userName } = authData;
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const navRef = useRef(null);
  const toggleRef = useRef(null);

  const firstName = userName?.trim() ? userName.trim().split(' ')[0] : 'Você';
  const isDark = theme === 'dark';

  const close = useCallback(() => setIsOpen(false), []);

  // Trocar de página fecha o menu.
  useEffect(() => {
    close();
  }, [location.pathname, close]);

  // Ao voltar para telas largas o painel deixa de existir: reinicia o estado
  // para não travar a rolagem da página.
  useEffect(() => {
    const media = window.matchMedia?.(DESKTOP_QUERY);

    if (!media) {
      return undefined;
    }

    const handleChange = (event) => {
      if (event.matches) {
        close();
      }
    };

    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [close]);

  // Bloqueia a rolagem do fundo e devolve o foco ao botão ao fechar.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const toggleButton = toggleRef.current;
    document.body.classList.add('has-overlay');
    navRef.current?.querySelector(FOCUSABLE)?.focus();

    return () => {
      document.body.classList.remove('has-overlay');
      toggleButton?.focus();
    };
  }, [isOpen]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      close();
      return;
    }

    if (event.key !== 'Tab' || !navRef.current) {
      return;
    }

    const focusable = Array.from(navRef.current.querySelectorAll(FOCUSABLE));

    if (focusable.length === 0) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <header className="app-header">
      <div className="app-header__inner">
        <Link className="app-brand" to="/">
          <span className="app-brand__mark" aria-hidden="true">
            <i className="bi bi-basket2-fill"></i>
          </span>
          <span className="app-brand__text">
            Lista de Compras
            <small>Organize, compartilhe e acompanhe</small>
          </span>
        </Link>

        <button
          type="button"
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label={isDark ? 'Ativar modo claro' : 'Ativar modo escuro'}
          title={isDark ? 'Modo claro' : 'Modo escuro'}
        >
          <i className={`bi ${isDark ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`} aria-hidden="true"></i>
        </button>

        <button
          ref={toggleRef}
          type="button"
          className="nav-toggle"
          onClick={() => setIsOpen((current) => !current)}
          aria-label={isOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={isOpen}
          aria-controls="app-nav"
        >
          <i className={`bi ${isOpen ? 'bi-x-lg' : 'bi-list'}`} aria-hidden="true"></i>
        </button>

        {/* Apenas um alvo de toque para fechar: quem usa teclado ou leitor de
            tela fecha pelo Esc ou pelo próprio botão do menu. */}
        {isOpen && <div className="nav-backdrop" aria-hidden="true" onClick={close} />}

        <nav
          id="app-nav"
          ref={navRef}
          className={`app-nav${isOpen ? ' is-open' : ''}`}
          aria-label="Navegação principal"
          onKeyDown={handleKeyDown}
        >
          <div className="app-nav__links">
            <NavLink className={navLinkClass} to="/" end>
              <i className="bi bi-house-door" aria-hidden="true"></i>
              Início
            </NavLink>
            {isAuthenticated && (
              <>
                <NavLink className={navLinkClass} to="/listas">
                  <i className="bi bi-list-check" aria-hidden="true"></i>
                  Minhas listas
                </NavLink>
                <NavLink className={navLinkClass} to="/perfil">
                  <i className="bi bi-person" aria-hidden="true"></i>
                  Perfil
                </NavLink>
              </>
            )}
          </div>

          <div className="app-nav__actions">
            {isAuthenticated ? (
              <>
                <span className="user-pill">
                  <i className="bi bi-person-circle" aria-hidden="true"></i>
                  <span>Olá, {firstName}</span>
                </span>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => logout()}>
                  <i className="bi bi-box-arrow-right" aria-hidden="true"></i>
                  Sair
                </button>
              </>
            ) : (
              <>
                <Link className="btn btn--ghost btn--sm" to="/register">
                  Criar conta
                </Link>
                <Link className="btn btn--primary btn--sm" to="/login">
                  Entrar
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
